import React, { useCallback, useEffect, useState } from 'react'
import ClipLoader from 'react-spinners/ClipLoader'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyGetWorkQuery, useLazyGetWorkAttachmentsQuery } from '../store/api'
import { convertWorkType } from '../utils'
import { connect } from 'react-redux'
import moment from 'moment'
import { clearWork } from '../store/slices'
import { OrganizationWorkData, WorkProductAttachmentData } from '../types'

interface WorkDetailsStateProps {
  work: OrganizationWorkData | null
}

interface WorkDetailsDispatchProps {
  clearWork: () => void
}

type WorkDetailsProps = WorkDetailsStateProps & WorkDetailsDispatchProps

const WorkDetails = ({ work, clearWork }: WorkDetailsProps): JSX.Element => {
  const navigate = useNavigate()

  const { workId } = useParams()
  const [getWork] = useLazyGetWorkQuery()
  const [getWorkAttachments] = useLazyGetWorkAttachmentsQuery()

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)

  const [attachmentList, setAttachmentList] = useState<WorkProductAttachmentData[]>([])

  const loadWorkData = useCallback(async () => {
    if (workId) {
      try {
        setIsApiRequestInProgress(true)

        await getWork(workId).unwrap()

        const workAttachments = await getWorkAttachments(workId).unwrap()
        setAttachmentList(workAttachments || [])

        setIsApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsApiRequestInProgress(false)
      }
    }
  }, [])

  useEffect(() => {
    clearWork()
    loadWorkData()
  }, [workId])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="row mt-3">
          <div className="d-flex align-items-center">
            <h3 className="mb-0 me-3">Work Details</h3>
            {isApiRequestInProgress ? (
              <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={20} />
              </div>
            ) : null}
          </div>
          {isApiRequestInProgress ? null : (
            <div className="d-flex flex-column">
              <table className="table table-borderless mt-4">
                <tbody>
                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">DOI</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      {work?.workProduct.doi ? (
                        <a
                          className="fw-normal fs-0"
                          href={`http://dx.doi.org/${work?.workProduct.doi}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {work?.workProduct.doi}
                        </a>
                      ) : null}
                    </td>

                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Title</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">{work?.workProduct.title}</h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Publisher</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-lg fw-normal text-800">{work?.workProduct.publisher}</h5>
                    </td>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Published Date</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-sm fw-normal text-800">
                        {work?.workProduct.publicationDate
                          ? moment(work.workProduct.publicationDate).format('MMM, YYYY')
                          : ''}
                      </h5>
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">ISBN</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-lg fw-normal text-800">{work?.workProduct.isbn}</h5>
                    </td>

                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">URL</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      {work?.workProduct.url ? (
                        <a className="fw-normal fs-0" href={work?.workProduct.url} target="_blank" rel="noreferrer">
                          {work?.workProduct.url}
                        </a>
                      ) : null}
                    </td>
                  </tr>

                  <tr>
                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Type</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <h5 className="lh-lg fw-normal text-800">
                        {work?.workProduct.workProductType ? convertWorkType(work?.workProduct.workProductType) : ''}
                      </h5>
                    </td>

                    <td className="py-2 ps-0">
                      <div className="d-flex">
                        <h5 className="lh-sm me-4">Grant Number</h5>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      {work?.grantId && work?.externalGrantId ? (
                        <button
                          className="btn-link fw-normal fs-0"
                          style={{ color: '#3874ff' }}
                          onClick={() => {
                            navigate(`/grants/${work.grantId}/details`)
                          }}
                        >
                          {work?.externalGrantId}
                        </button>
                      ) : null}
                      {!work?.grantId && work?.externalGrantId ? (
                        <h5 className="lh-lg fw-normal text-800">{work?.externalGrantId}</h5>
                      ) : null}
                    </td>
                  </tr>
                </tbody>
              </table>
              {attachmentList.length > 0 ? (
                <div className="row gy-3 mt-3 justify-content-between">
                  <h3 className="mb-1 text-1100">Attachment List</h3>
                  <ol className="mt-4">
                    {attachmentList.map((attachmentData: WorkProductAttachmentData, i: number) => (
                      <li key={i} className="d-flex align-items-center">
                        <a href={attachmentData.url} target="_blank" rel="noreferrer">
                          {attachmentData.name}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

const mapStateToProps = (state: any): WorkDetailsStateProps => {
  return {
    work: state.grant.work,
  }
}

const mapDispatchToProps = (dispatch: any): WorkDetailsDispatchProps => {
  return {
    clearWork: () => dispatch(clearWork()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkDetails)
